import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Spinner } from "../../components/States";

// Developer surface: API keys and the embeddable badge.
//
// A created key is shown ONCE. The UI has to say so plainly, because there is
// no recovery path — only the hash is stored.

export function Developer() {
  const [keys, setKeys] = useState<Awaited<ReturnType<typeof api.dashKeys>> | null>(null);
  const [name, setName] = useState("");
  const [fresh, setFresh] = useState<string | null>(null);
  const [entities, setEntities] = useState<Awaited<ReturnType<typeof api.dashEntities>>["items"]>([]);
  const [snippet, setSnippet] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const load = () => api.dashKeys().then(setKeys).catch(() => setKeys({ items: [], entitled: false }));
  useEffect(() => {
    void load();
    api.dashEntities().then((r) => setEntities(r.items)).catch(() => setEntities([]));
  }, []);

  const create = async () => {
    setErr(null);
    try {
      const created = await api.dashCreateKey(name || "Untitled key");
      setFresh(created.key);
      setName("");
      void load();
    } catch (e) {
      setErr((e as Error).message);
    }
  };

  if (!keys) return <Spinner />;

  return (
    <div className="narrow-md">
      <h2>Developer</h2>

      {!keys.entitled && (
        <p className="muted">
          API access and the embeddable badge are part of the Pro plan. Your listings are on the
          free plan at the moment.
        </p>
      )}

      <h3 className="section">API keys</h3>
      {keys.items.length === 0 ? (
        <p className="muted">No keys yet.</p>
      ) : (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Name</th><th>Key</th><th>Last used</th><th /></tr>
            </thead>
            <tbody>
              {keys.items.map((k) => (
                <tr key={k.id}>
                  <td>{k.name}</td>
                  <td className="mono">{k.prefix ?? "—"}…</td>
                  <td className="num">{k.lastUsedAt?.slice(0, 10) ?? "never"}</td>
                  <td>
                    {k.revokedAt ? (
                      <span className="muted">revoked</span>
                    ) : (
                      <button
                        className="btn btn-ghost btn-sm"
                        onClick={() => void api.dashRevokeKey(k.id).then(load)}
                      >
                        Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {keys.entitled && (
        <div className="row gap-8 mt-10">
          <input
            className="input flex-1"
            placeholder="Key name (e.g. production)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <button className="btn btn-primary" onClick={() => void create()}>Create key</button>
        </div>
      )}

      {fresh && (
        <div className="card u-c964eae">
          <p className="u-ad7f18b">
            <strong>Copy this now — it is the only time you will see it.</strong> We store only a
            hash, so it cannot be shown again.
          </p>
          <pre className="mono u-3a95f43">{fresh}</pre>
        </div>
      )}
      {err && <p className="u-497726e">{err}</p>}

      <h3 className="section">Embeddable badge</h3>
      {entities.length === 0 ? (
        <p className="muted">Claim a listing to get its badge.</p>
      ) : (
        <div className="grid gap-8">
          {entities.map((e) => (
            <div key={e.id} className="row u-c58a824">
              <span className="flex-1">{e.name}</span>
              <button
                className="btn btn-ghost btn-sm"
                onClick={() =>
                  void api
                    .dashBadgeSnippet(e.id)
                    .then((r) => setSnippet(r.snippet))
                    .catch((x) => setErr((x as Error).message))
                }
              >
                Get badge
              </button>
            </div>
          ))}
        </div>
      )}

      {snippet && (
        <div className="card u-6e4e42f">
          <p className="muted u-4eca5c6">
            The badge shows your live rating — including a poor one. It is a citation, not an advert.
          </p>
          <pre className="mono u-3a95f43">{snippet}</pre>
          <button
            className="btn btn-ghost btn-sm mt-8"
            onClick={() => void navigator.clipboard?.writeText(snippet)}
          >
            Copy snippet
          </button>
        </div>
      )}
    </div>
  );
}
